import { ButtonBase, Icon, Tooltip } from '@fullstory/components';
import React, { ComponentPropsWithoutRef, forwardRef } from 'react';
import { useIntl } from 'react-intl';
import { classNames } from '@fullstory/util/fmt';
import css from './index.module.scss';

export interface StarToggleProps
  extends Omit<ComponentPropsWithoutRef<typeof ButtonBase>, 'title' | 'className'> {
  starred: boolean;
  size?: ComponentPropsWithoutRef<typeof Icon>['size'];
}

export const StarToggle = forwardRef<HTMLElement, StarToggleProps>(
  (
    {
      starred,
      onClick,
      size = 16,
      testId = 'star-button',
      disabled = false,
      ...rest
    }: StarToggleProps,
    ref,
  ) => {
    const intl = useIntl();

    const iconName = starred ? 'star-filled' : 'star-unfilled';
    const labelText = starred
      ? intl.formatMessage({
          defaultMessage: 'Unstar',
          id: 'iKdIxz',
          description: 'Remove from starred items(favorites)',
        })
      : intl.formatMessage({
          defaultMessage: 'Star',
          id: 'UmbbmC',
          description: 'Add to starred items(favorites)',
        });
    const intentClass = starred ? css['star-filled'] : css['star-unfilled'];
    const sizeClass = size === 16 ? css['small'] : css['large'];

    return (
      <Tooltip
        data-component="StarToggle"
        placement="bottom"
        tooltipContent={labelText}
        showDelay={300}
        persistDelay={0}
        offset={12}
        compact
        interactive={false}
      >
        <ButtonBase
          {...rest}
          aria-label={labelText}
          ref={ref}
          onClick={onClick}
          testId={testId}
          disabled={disabled}
          className={classNames(css.button, intentClass, sizeClass)}
        >
          <Icon name={iconName} size={size} color={disabled ? 'disabled' : 'inherit'} />
        </ButtonBase>
      </Tooltip>
    );
  },
);

StarToggle.displayName = 'StarToggle';
